import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import type { RootState } from '@/store'
import type {
  CompletedEnrollment,
  CompletedEnrollmentFilters,
  CompletedEnrollmentResult,
  CompletedEnrollmentSort,
  CompletedEnrollmentState,
} from '@/types/completed-enrollment.types'

const initialFilters: CompletedEnrollmentFilters = {
  query: '',
  verificationLevel: [],
  completedBy: [],
  hasCredentials: false,
}

const initialSort: CompletedEnrollmentSort = {
  field: 'completedAt',
  direction: 'desc',
}

const initialState: CompletedEnrollmentState = {
  // Search results
  results: null,

  // Current filters
  filters: initialFilters,

  // Sorting
  sort: initialSort,

  // UI state
  isLoading: false,
  isSearching: false,

  // Selected enrollments
  selectedEnrollments: [],

  // View mode
  viewMode: 'table',

  // Error handling
  error: null,

  // Refresh settings
  autoRefresh: false,
  refreshInterval: 60, // 60 seconds
  lastRefreshed: null,

  // Export state
  isExporting: false,

  // Analytics state
  analyticsView: 'overview',
}

const completedEnrollmentSlice = createSlice({
  name: 'completedEnrollment',
  initialState,
  reducers: {
    // Search operations
    setSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
      state.isSearching = action.payload
      if (action.payload) {
        state.error = null
      }
    },

    setSearchResults: (state, action: PayloadAction<CompletedEnrollmentResult>) => {
      state.results = action.payload
      state.isLoading = false
      state.isSearching = false
      state.error = null
      state.lastRefreshed = new Date().toISOString()
    },

    setSearchError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isLoading = false
      state.isSearching = false
    },

    clearSearchResults: (state) => {
      state.results = null
      state.error = null
    },

    // Filter management
    updateFilters: (state, action: PayloadAction<Partial<CompletedEnrollmentFilters>>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      }
    },

    resetFilters: (state) => {
      state.filters = initialFilters
      state.sort = initialSort
    },

    setQuickSearch: (state, action: PayloadAction<string>) => {
      state.filters = {
        ...initialFilters,
        query: action.payload,
      }
    },

    // Sorting
    updateSort: (state, action: PayloadAction<CompletedEnrollmentSort>) => {
      state.sort = action.payload
    },

    toggleSortDirection: (state, action: PayloadAction<CompletedEnrollmentSort['field']>) => {
      if (state.sort.field === action.payload) {
        state.sort.direction = state.sort.direction === 'asc' ? 'desc' : 'asc'
      } else {
        state.sort = {
          field: action.payload,
          direction: 'asc',
        }
      }
    },

    // Selection management
    setSelectedEnrollments: (state, action: PayloadAction<string[]>) => {
      state.selectedEnrollments = action.payload
    },

    toggleEnrollmentSelection: (state, action: PayloadAction<string>) => {
      const enrollmentId = action.payload
      if (state.selectedEnrollments.includes(enrollmentId)) {
        state.selectedEnrollments = state.selectedEnrollments.filter((id) => id !== enrollmentId)
      } else {
        state.selectedEnrollments.push(enrollmentId)
      }
    },

    selectAllEnrollments: (state) => {
      if (state.results) {
        state.selectedEnrollments = state.results.enrollments.map((e) => e.id)
      }
    },

    clearSelection: (state) => {
      state.selectedEnrollments = []
    },

    // UI state
    setViewMode: (state, action: PayloadAction<CompletedEnrollmentState['viewMode']>) => {
      state.viewMode = action.payload
    },

    setAnalyticsView: (state, action: PayloadAction<CompletedEnrollmentState['analyticsView']>) => {
      state.analyticsView = action.payload
    },

    // Auto refresh settings
    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.autoRefresh = action.payload
    },

    setRefreshInterval: (state, action: PayloadAction<number>) => {
      state.refreshInterval = action.payload
    },

    // Export state
    setExporting: (state, action: PayloadAction<boolean>) => {
      state.isExporting = action.payload
    },

    // Update single enrollment in results
    updateEnrollmentInResults: (
      state,
      action: PayloadAction<{
        enrollmentId: string
        updates: Partial<CompletedEnrollment>
      }>
    ) => {
      if (state.results) {
        const index = state.results.enrollments.findIndex(
          (e) => e.id === action.payload.enrollmentId
        )
        if (index >= 0) {
          const target = state.results.enrollments[index]
          if (target) {
            Object.assign(target, action.payload.updates)
          }
        }
      }
    },

    // Remove enrollment from results
    removeEnrollmentFromResults: (state, action: PayloadAction<string>) => {
      if (state.results) {
        state.results.enrollments = state.results.enrollments.filter((e) => e.id !== action.payload)
        state.results.totalCount -= 1

        // Update selection if the removed enrollment was selected
        state.selectedEnrollments = state.selectedEnrollments.filter((id) => id !== action.payload)
      }
    },

    // Pagination
    updatePage: (state, action: PayloadAction<number>) => {
      if (state.results) {
        state.results.page = action.payload
      }
    },

    // Clear all state
    resetCompletedEnrollmentState: (_state) => {
      return initialState
    },
  },
})

// Export actions
export const {
  setSearchLoading,
  setSearchResults,
  setSearchError,
  clearSearchResults,
  updateFilters,
  resetFilters,
  setQuickSearch,
  updateSort,
  toggleSortDirection,
  setSelectedEnrollments,
  toggleEnrollmentSelection,
  selectAllEnrollments,
  clearSelection,
  setViewMode,
  setAnalyticsView,
  setAutoRefresh,
  setRefreshInterval,
  setExporting,
  updateEnrollmentInResults,
  removeEnrollmentFromResults,
  updatePage,
  resetCompletedEnrollmentState,
} = completedEnrollmentSlice.actions

// Selectors
export const selectCompletedEnrollment = (state: RootState) => state.completedEnrollment
export const selectCompletedResults = (state: RootState) => state.completedEnrollment.results
export const selectCompletedFilters = (state: RootState) => state.completedEnrollment.filters
export const selectCompletedSort = (state: RootState) => state.completedEnrollment.sort
export const selectSelectedEnrollments = (state: RootState) =>
  state.completedEnrollment.selectedEnrollments
export const selectIsSearching = (state: RootState) => state.completedEnrollment.isSearching
export const selectCompletedError = (state: RootState) => state.completedEnrollment.error
export const selectViewMode = (state: RootState) => state.completedEnrollment.viewMode
export const selectAnalyticsView = (state: RootState) => state.completedEnrollment.analyticsView
export const selectAutoRefresh = (state: RootState) => state.completedEnrollment.autoRefresh
export const selectRefreshInterval = (state: RootState) => state.completedEnrollment.refreshInterval
export const selectLastRefreshed = (state: RootState) => state.completedEnrollment.lastRefreshed
export const selectIsExporting = (state: RootState) => state.completedEnrollment.isExporting

// Computed selectors
export const selectHasCompletedResults = (state: RootState) =>
  state.completedEnrollment.results && state.completedEnrollment.results.enrollments.length > 0

export const selectTotalCompletedCount = (state: RootState) =>
  state.completedEnrollment.results?.totalCount || 0

export const selectCurrentPage = (state: RootState) => state.completedEnrollment.results?.page || 1

export const selectTotalPages = (state: RootState) =>
  state.completedEnrollment.results?.totalPages || 0

export const selectHasNextPage = (state: RootState) =>
  state.completedEnrollment.results?.hasNext || false

export const selectHasPreviousPage = (state: RootState) =>
  state.completedEnrollment.results?.hasPrevious || false

export const selectSelectedCount = (state: RootState) =>
  state.completedEnrollment.selectedEnrollments.length

export const selectIsAllSelected = (state: RootState) => {
  const results = state.completedEnrollment.results
  const selected = state.completedEnrollment.selectedEnrollments

  if (!results || results.enrollments.length === 0) return false
  return selected.length === results.enrollments.length
}

export const selectActiveFiltersCount = (state: RootState) => {
  const filters = state.completedEnrollment.filters
  let count = 0

  if (filters.query?.trim()) count++
  if (filters.digitalIdNumber?.trim()) count++
  if (filters.verificationLevel && filters.verificationLevel.length > 0) count++
  if (filters.completedBy && filters.completedBy.length > 0) count++
  if (filters.verifiedBy && filters.verifiedBy.length > 0) count++
  if (filters.qualityScoreMin !== undefined) count++
  if (filters.qualityScoreMax !== undefined) count++
  if (filters.completedFrom?.trim()) count++
  if (filters.completedTo?.trim()) count++
  if (filters.verifiedFrom?.trim()) count++
  if (filters.verifiedTo?.trim()) count++
  if (filters.region?.trim()) count++
  if (filters.zone?.trim()) count++
  if (filters.woreda?.trim()) count++
  if (filters.kebele?.trim()) count++
  if (filters.hasCredentials !== undefined) count++
  if (filters.credentialType && filters.credentialType.length > 0) count++
  if (filters.processingTimeMin !== undefined) count++
  if (filters.processingTimeMax !== undefined) count++

  return count
}

export const selectIsFilterActive = (state: RootState) => selectActiveFiltersCount(state) > 0

export const selectCompletedSummary = (state: RootState) =>
  state.completedEnrollment.results?.summary

// Export reducer
export default completedEnrollmentSlice.reducer
